import React, { useState } from 'react';
import { useAuth } from '../../context/AuthContext';
import { useFamily } from '../../context/FamilyContext';
import { createChore, updateChore } from '@coparent/shared/src/services/choreService';
import { DAYS_OF_WEEK, CHORE_FREQUENCIES, CHORE_SUGGESTIONS } from '@coparent/shared/src/utils/constants';
import ChildMultiSelect from '../common/ChildMultiSelect';
import { HiX } from 'react-icons/hi';
import toast from 'react-hot-toast';

const ChoreForm = ({ onClose, chore }) => {
  const { currentUser } = useAuth();
  const { familyId, familyChildren, childrenMap } = useFamily();
  const [loading, setLoading] = useState(false);
  const [name, setName] = useState(chore?.name || '');
  const [description, setDescription] = useState(chore?.description || '');
  const [frequency, setFrequency] = useState(chore?.frequency || 'daily');
  const [selectedDays, setSelectedDays] = useState(chore?.dayOfWeek || []);
  const [selectedChildIds, setSelectedChildIds] = useState(chore?.childIds || []);
  const isEditing = !!chore;

  const toggleDay = (dayIndex) => {
    setSelectedDays(prev =>
      prev.includes(dayIndex) ? prev.filter(d => d !== dayIndex) : [...prev, dayIndex]
    );
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!name.trim()) {
      toast.error('Chore name is required');
      return;
    }
    setLoading(true);
    try {
      const data = {
        name: name.trim(),
        description: description.trim() || null,
        frequency,
        dayOfWeek: frequency !== 'daily' ? selectedDays : [],
        childIds: selectedChildIds,
      };

      if (isEditing) {
        await updateChore(familyId, chore.id, data);
        toast.success('Chore updated!');
      } else {
        data.createdBy = currentUser.uid;
        await createChore(familyId, data);
        toast.success('Chore added!');
      }
      onClose();
    } catch (error) {
      toast.error(isEditing ? 'Failed to update chore' : 'Failed to add chore');
    }
    setLoading(false);
  };

  return (
    <div className="modal-overlay">
      <div className="modal-content max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between mb-5">
          <h3 className="text-lg font-bold text-ink-900">{isEditing ? 'Edit Chore' : 'Add Chore'}</h3>
          <button onClick={onClose} className="w-8 h-8 rounded-lg flex items-center justify-center text-ink-400 hover:bg-surface-100 hover:text-ink-600 transition-colors">
            <HiX className="text-lg" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-xs font-semibold text-ink-600 mb-1.5 uppercase tracking-wide">Chore Name</label>
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              className="input-base"
              placeholder="e.g., Make bed"
            />
          </div>

          <div>
            <label className="block text-xs font-semibold text-ink-600 mb-1.5 uppercase tracking-wide">Details (optional)</label>
            <textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              className="input-base"
              rows={2}
              placeholder="Any specific instructions..."
            />
          </div>

          <div>
            <label className="block text-xs font-semibold text-ink-600 mb-2 uppercase tracking-wide">Frequency</label>
            <div className="flex gap-2">
              {CHORE_FREQUENCIES.map(f => (
                <button
                  key={f.value}
                  type="button"
                  onClick={() => setFrequency(f.value)}
                  className={`flex-1 py-2 text-xs font-bold rounded-xl transition-all duration-200 ${
                    frequency === f.value
                      ? 'bg-ink-800 text-white shadow-warm-sm'
                      : 'bg-surface-100 text-ink-500 hover:bg-surface-200'
                  }`}
                >
                  {f.label}
                </button>
              ))}
            </div>
          </div>

          {frequency !== 'daily' && (
            <div>
              <label className="block text-xs font-semibold text-ink-600 mb-2 uppercase tracking-wide">Days</label>
              <div className="flex gap-1.5">
                {DAYS_OF_WEEK.map((day, i) => (
                  <button
                    key={i}
                    type="button"
                    onClick={() => toggleDay(i)}
                    className={`flex-1 py-2 text-xs font-bold rounded-xl transition-all duration-200 ${
                      selectedDays.includes(i)
                        ? 'bg-ink-800 text-white shadow-warm-sm'
                        : 'bg-surface-100 text-ink-500 hover:bg-surface-200'
                    }`}
                  >
                    {day.slice(0, 3)}
                  </button>
                ))}
              </div>
            </div>
          )}

          {familyChildren.length > 0 && (
            <ChildMultiSelect
              familyChildren={familyChildren}
              childrenMap={childrenMap}
              selectedChildIds={selectedChildIds}
              onChange={setSelectedChildIds}
            />
          )}

          {/* Suggestions (only when creating) */}
          {!isEditing && (
            <div>
              <label className="block text-xs font-semibold text-ink-600 mb-2 uppercase tracking-wide">Suggestions</label>
              <div className="flex gap-2 flex-wrap">
                {CHORE_SUGGESTIONS.map(s => (
                  <button
                    key={s.name}
                    type="button"
                    onClick={() => setName(s.name)}
                    className="px-3 py-1.5 rounded-full text-xs font-medium bg-surface-100 text-ink-500 hover:bg-surface-200 transition-colors"
                  >
                    {s.name} <span className="text-ink-300 ml-0.5">{s.ageRange}</span>
                  </button>
                ))}
              </div>
            </div>
          )}

          <div className="flex gap-3 pt-3">
            <button type="button" onClick={onClose} className="btn-secondary flex-1">
              Cancel
            </button>
            <button type="submit" disabled={loading} className="btn-primary flex-1">
              {loading ? (isEditing ? 'Saving...' : 'Adding...') : (isEditing ? 'Save Changes' : 'Add Chore')}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default ChoreForm;
